// Misteck Matrix Vault v3.0 — JWT-gated gallery API client.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/gallery`;
const token = () => localStorage.getItem("mist_token") || "";
const authHeaders = () => (token() ? { Authorization: `Bearer ${token()}` } : {});

const EXT_FROM_MIME = {
  "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp",
  "video/mp4": "mp4", "video/webm": "webm", "video/quicktime": "mov", "video/x-m4v": "m4v", "video/3gpp": "mp4",
  "audio/mpeg": "mp3", "audio/wav": "wav", "audio/x-wav": "wav", "audio/mp4": "m4a", "audio/aac": "aac", "audio/x-m4a": "m4a",
};

// Accepts a File or a filename string. Falls back to the file's MIME type so
// extension-less mobile picks (Samsung/Android content:// URIs) still resolve.
export const TRACK_FOR_EXT = (fileOrName = "") => {
  const name = typeof fileOrName === "string" ? fileOrName : fileOrName?.name || "";
  const mime = typeof fileOrName === "string" ? "" : (fileOrName?.type || "").toLowerCase();
  let ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (!ext && mime) ext = EXT_FROM_MIME[mime] || "";
  if (["png", "jpg", "jpeg", "webp"].includes(ext) || mime.startsWith("image/")) return "IMAGES";
  if (["mp4", "webm", "mov", "m4v"].includes(ext) || mime.startsWith("video/")) return "VIDEOS";
  if (["mp3", "wav", "m4a", "aac"].includes(ext) || mime.startsWith("audio/")) return "AUDIO";
  return null;
};

// Resolve a safe extension for a File (name first, then MIME).
export const EXT_FOR_FILE = (file) => {
  const name = file?.name || "";
  if (name.includes(".")) return name.split(".").pop().toLowerCase();
  return EXT_FROM_MIME[(file?.type || "").toLowerCase()] || "";
};

export function resolveSrc(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api")) return BACKEND + path;
  return path;
}
export function publicUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api")) return BACKEND + path;
  return window.location.origin + path;
}

// Local-device save — forces a manual filename before extracting the blob.
export async function downloadWithName(item, filename) {
  const url = item.path.startsWith("/api") ? resolveSrc(item.path) + "?download=1" : resolveSrc(item.path);
  const res = await fetch(url, { headers: item.path.startsWith("/api") ? authHeaders() : {} });
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename.includes(".") ? filename : `${filename}.${item.format}`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

export async function fetchManifest(scope = "public") {
  const r = await fetch(`${API}/manifest?scope=${scope}`, { headers: authHeaders() });
  if (!r.ok) throw new Error("Failed to load gallery manifest");
  return r.json();
}

export async function whoami() {
  try { const r = await fetch(`${API}/whoami`, { headers: authHeaders() }); return r.ok ? r.json() : { is_founder: false }; }
  catch { return { is_founder: false }; }
}

export async function uploadMedia({ file, key, category, track, destination, ext }) {
  const fd = new FormData();
  // Send a filename with a guaranteed extension so extension-less mobile picks
  // (Samsung content:// URIs) validate server-side.
  const safeName = ext && !(file.name || "").toLowerCase().endsWith("." + ext) ? `${(key || "asset")}.${ext}` : file.name;
  fd.append("file", file, safeName);
  fd.append("key", key); fd.append("category", category);
  fd.append("track", track); fd.append("destination", destination);
  let r;
  try {
    r = await fetch(`${API}/upload`, { method: "POST", headers: authHeaders(), body: fd });
  } catch (netErr) {
    const mb = (file.size / 1e6).toFixed(1);
    throw new Error(`Network error uploading ${mb} MB — check your connection and retry`);
  }
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `Upload failed (HTTP ${r.status})`); }
  return r.json();
}

export async function importManifest(items) {
  const r = await fetch(`${API}/import-manifest`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ items }) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `Import failed (HTTP ${r.status})`); }
  return r.json();
}

export async function deleteMedia(id) {
  const r = await fetch(`${API}/media/${id}`, { method: "DELETE", headers: authHeaders() });
  if (!r.ok) throw new Error("Delete failed");
  return r.json();
}

export async function renameMedia(id, key) {
  const r = await fetch(`${API}/media/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ key }) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Rename failed"); }
  return r.json();
}

export async function autoSortImages() {
  let sorted = 0;
  for (let pass = 0; pass < 20; pass++) {   // batches of 6 until the Uploads inbox is clear
    const r = await fetch(`${API}/auto-sort`, { method: "POST", headers: authHeaders() });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Auto-sort failed"); }
    const d = await r.json();
    sorted += d.sorted || 0;
    if (!d.remaining) break;
  }
  return { sorted };
}

// DB-hygiene: broken-fix + dedupe + name-sequence over Stock Library IMAGES.
// Batched server-side; loop until done. onProgress({processed, remaining}) optional.
export async function cleanupLibrary(onProgress) {
  const totals = { processed: 0, broken: 0, resized: 0, deduped: 0, renamed: 0 };
  for (let pass = 0; pass < 60; pass++) {
    const r = await fetch(`${API}/cleanup`, { method: "POST", headers: authHeaders() });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Cleanup failed"); }
    const d = await r.json();
    totals.processed += d.processed || 0;
    totals.broken += d.broken || 0;
    totals.resized += d.resized || 0;
    totals.deduped = d.deduped || totals.deduped;
    totals.renamed = d.renamed || totals.renamed;
    if (onProgress) onProgress(d);
    if (d.done) break;
  }
  return totals;
}

export async function fetchAlbums(track = "") {
  const r = await fetch(`${API}/albums${track ? `?track=${track}` : ""}`, { headers: authHeaders() });
  if (!r.ok) throw new Error("Failed to load albums");
  return r.json();
}
export async function createAlbum({ name, track }) {
  const r = await fetch(`${API}/albums`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ name, track }) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Could not create album"); }
  return r.json();
}
export async function deleteAlbum(id) {
  const r = await fetch(`${API}/albums/${id}`, { method: "DELETE", headers: authHeaders() });
  if (!r.ok) throw new Error("Delete failed");
  return r.json();
}
export async function transferMedia({ media_id, key, category }) {
  const r = await fetch(`${API}/transfer`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ media_id, key, category }) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Transfer failed"); }
  return r.json();
}

export async function getPrefs() {
  const r = await fetch(`${API}/prefs`, { headers: authHeaders() });
  if (!r.ok) throw new Error("prefs");
  return r.json();
}
export async function setPrefs(payload) {
  const r = await fetch(`${API}/prefs`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(payload) });
  if (!r.ok) throw new Error("prefs");
  return r.json();
}
